import { useCallback, useEffect, useMemo, useState } from "react";
import { AUTH_PROVIDER } from "@no-busy-work/auth/protocol";
import { ApprovalState, IncidentDecisionState, type CreateIncidentRequest } from "@no-busy-work/ops-bridge-protocol";
import { AUTH_PROVIDER_DISABLED, CLOSEOUT_GATE, CUSTOM_SERVICE_OPTION, GRAPH_MODE, PREVIEW_FIXTURE_FALLBACK_MODE } from "../OpsCenter.Config";
import { incidentCreateCatalog, optionsForApp, selectedCatalogAppId } from "../OpsCenter.Catalog";
import { actionRows, buildAiQuestionThread, resolveAiQuestionKind } from "../OpsCenter.Data";
import { formatCopy } from "../OpsCenter.Format";
import { IncidentApprovalTransition, TAB_ID } from "../OpsCenter.Types";
import type { AiQuestionKind, CreateIncidentForm, HealthResponse, IncidentApprovalTransitionRequest, IncidentQueueFilters, OpsCenterActions, OpsCenterCopy, OpsCenterState, PublicAuthSession, TabId, GraphMode } from "../OpsCenter.Types";
import { readableError } from "../i18n/OpsCenter.I18n";
import { forgeIncidentHandoffFromSearch } from "../OpsCenter.Handoff";
import { opsApi } from "../../../services/opsApi";

const initialCreateForm: CreateIncidentForm = {
  rawSummary: "Phone lines are down for customers in production.",
  appId: "nbw",
  source: "operator_chat",
  serviceHint: "communications.phone_lines",
  customServiceHint: "",
  environment: "production",
  customerLocale: "en",
  customerAudienceRef: "affected-customers",
  ownerRef: "employee:maya-chen",
  severity: "sev2"
};

const initialQueueFilters: IncidentQueueFilters = {
  appId: "all",
  environment: "all",
  severity: "all",
  status: "all",
  sort: "priority"
};

const initialState: OpsCenterState = {
  session: null,
  health: null,
  incident: null,
  incidentQueue: [],
  busy: true,
  notice: "",
  error: "",
  operatorAccessCode: "",
  activeTab: TAB_ID.Overview,
  graphMode: GRAPH_MODE.Incident,
  graphFocusId: "",
  timelinePosition: 0,
  toolDrawerOpen: false,
  settingsOpen: false,
  toolSearch: "",
  aiOpen: false,
  aiQuestionDraft: "",
  aiThreads: [],
  createOpen: false,
  create: initialCreateForm,
  lookupIncidentId: "",
  queueFilters: initialQueueFilters,
  rootCause: "Restored the affected route and verified customer calls.",
  remediationProof: "Bounded remediation completed and attached to the runbook output.",
  verificationEvidence: "Synthetic health probe passed for the affected customer path.",
  customerSafeSummary: "Service recovered and affected customers can retry normal workflows.",
  reopenReason: "Customer-impact signal returned after closeout."
};

export interface OpsCenterController {
  readonly state: OpsCenterState;
  readonly actions: OpsCenterActions;
}

export function useOpsCenterState(copy: OpsCenterCopy): OpsCenterController {
  const [state, setState] = useState<OpsCenterState>(initialState);
  const handoffIncidentId = useMemo(
    () => forgeIncidentHandoffFromSearch(typeof window === "undefined" ? "" : window.location.search),
    []
  );

  useEffect(() => {
    let cancelled = false;
    async function boot(): Promise<void> {
      const [session, health] = await Promise.all([loadSession(), loadHealth()]);
      const incidentQueue = session.authenticated ? await loadIncidentQueue(session, initialQueueFilters, "") : [];
      const initialIncident = session.authenticated
        ? await loadInitialIncident(session, health, incidentQueue, handoffIncidentId)
        : { detail: null, handoffFailed: false };
      if (cancelled) {
        return;
      }
      setState((current) => ({
        ...current,
        session,
        health,
        incident: initialIncident.handoffFailed ? null : initialIncident.detail ?? current.incident,
        incidentQueue,
        lookupIncidentId: handoffIncidentId ?? current.lookupIncidentId,
        error: initialIncident.handoffFailed ? copy.notices.incidentHandoffFailed : current.error,
        busy: false
      }));
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, [copy.notices.incidentHandoffFailed, handoffIncidentId]);

  const runBusy = useCallback(
    async (task: () => Promise<Partial<OpsCenterState> | void>): Promise<void> => {
      setState((current) => ({ ...current, busy: true, error: "", notice: "" }));
      try {
        const patch = await task();
        setState((current) => ({ ...current, ...(patch ?? {}), busy: false }));
      } catch (error) {
        setState((current) => ({
          ...current,
          ...(isAuthenticationError(error) ? { session: unauthenticatedSession(current.session?.provider ?? AUTH_PROVIDER_DISABLED) } : {}),
          error: readableError(error),
          busy: false
        }));
      }
    },
    []
  );

  const actions = useMemo<OpsCenterActions>(
    () => ({
      setOperatorAccessCode: (operatorAccessCode) => setState((current) => ({ ...current, operatorAccessCode })),
      setCreateField: (name, value) =>
        setState((current) => ({
          ...current,
          create: updateCreateForm(current.create, name, value, current.health)
        })),
      setLookupIncidentId: (lookupIncidentId) => setState((current) => ({ ...current, lookupIncidentId })),
      setRootCause: (rootCause) => setState((current) => ({ ...current, rootCause })),
      setRemediationProof: (remediationProof) => setState((current) => ({ ...current, remediationProof })),
      setVerificationEvidence: (verificationEvidence) => setState((current) => ({ ...current, verificationEvidence })),
      setCustomerSafeSummary: (customerSafeSummary) => setState((current) => ({ ...current, customerSafeSummary })),
      setReopenReason: (reopenReason) => setState((current) => ({ ...current, reopenReason })),
      setActiveTab: (activeTab: TabId) => setState((current) => ({ ...current, activeTab })),
      setGraphMode: (graphMode: GraphMode) => setState((current) => ({ ...current, graphMode, graphFocusId: "" })),
      setGraphFocusId: (graphFocusId) => setState((current) => ({ ...current, graphFocusId })),
      setTimelinePosition: (timelinePosition) => setState((current) => ({ ...current, timelinePosition })),
      openToolDrawer: () => setState((current) => ({ ...current, toolDrawerOpen: true, settingsOpen: false, aiOpen: false })),
      closeToolDrawer: () => setState((current) => ({ ...current, toolDrawerOpen: false })),
      openSettings: () => setState((current) => ({ ...current, settingsOpen: true, toolDrawerOpen: false, aiOpen: false })),
      closeSettings: () => setState((current) => ({ ...current, settingsOpen: false })),
      setToolSearch: (toolSearch) => setState((current) => ({ ...current, toolSearch })),
      toggleAi: () => setState((current) => ({ ...current, aiOpen: !current.aiOpen, settingsOpen: false, toolDrawerOpen: current.aiOpen ? current.toolDrawerOpen : false })),
      openAi: () => setState((current) => ({ ...current, aiOpen: true, settingsOpen: false, toolDrawerOpen: false })),
      closeAi: () => setState((current) => ({ ...current, aiOpen: false })),
      setAiQuestionDraft: (aiQuestionDraft) => setState((current) => ({ ...current, aiQuestionDraft })),
      askAiQuestion: (question?: string, kind?: AiQuestionKind) =>
        setState((current) => {
          const aiQuestion = (question ?? current.aiQuestionDraft).trim();
          if (!aiQuestion) {
            return { ...current, aiOpen: true };
          }
          const thread = buildAiQuestionThread(
            aiQuestion,
            kind ?? resolveAiQuestionKind(aiQuestion),
            current.incident,
            copy,
            current.aiThreads.length + 1
          );
          return {
            ...current,
            aiOpen: true,
            aiQuestionDraft: "",
            aiThreads: [thread, ...current.aiThreads].slice(0, 5)
          };
        }),
      setCreateOpen: (createOpen) => setState((current) => ({ ...current, createOpen })),
      setQueueFilter: (name, value) =>
        setState((current) => ({
          ...current,
          queueFilters: updateQueueFilters(current.queueFilters, name, value)
        })),
      loginWithOperatorAccessCode: async () => {
        const accessCode = state.operatorAccessCode.trim();
        if (!accessCode) {
          setState((current) => ({ ...current, error: copy.auth.accessCodeRequired }));
          return;
        }
        await runBusy(async () => {
          const session = await opsApi.login(accessCode);
          const health = await loadHealth();
          const incidentQueue = session.authenticated ? await loadIncidentQueue(session, initialQueueFilters, "") : [];
          const initialIncident = session.authenticated
            ? await loadInitialIncident(session, health, incidentQueue, handoffIncidentId)
            : { detail: null, handoffFailed: false };
          return {
            session,
            health,
            incident: initialIncident.handoffFailed ? null : initialIncident.detail ?? state.incident,
            incidentQueue,
            lookupIncidentId: handoffIncidentId ?? state.lookupIncidentId,
            operatorAccessCode: "",
            aiQuestionDraft: "",
            aiThreads: [],
            notice: "",
            error: initialIncident.handoffFailed ? copy.notices.incidentHandoffFailed : ""
          };
        });
      },
      logout: async () => {
        await runBusy(async () => {
          await opsApi.logout(state.session);
          const session = await loadSession();
          return {
            session,
            incident: null,
            incidentQueue: [],
            lookupIncidentId: "",
            aiQuestionDraft: "",
            aiThreads: [],
            settingsOpen: false,
            notice: copy.notices.signedOut
          };
        });
      },
      createIncident: async () => {
        await runBusy(async () => {
          const response = await opsApi.createIncident(state.session, createIncidentRequest(state.create));
          const incidentQueue = await loadIncidentQueue(state.session, state.queueFilters, "");
          return {
            incident: response.detail,
            incidentQueue,
            lookupIncidentId: "",
            activeTab: TAB_ID.Overview,
            createOpen: false,
            aiQuestionDraft: "",
            aiThreads: [],
            notice: copy.notices.incidentCreated
          };
        });
      },
      loadIncident: async () => {
        const incidentId = state.lookupIncidentId.trim();
        if (!incidentId) {
          setState((current) => ({ ...current, error: copy.notices.incidentIdRequired }));
          return;
        }
        await runBusy(async () => {
          const response = await opsApi.loadIncident(state.session, incidentId);
          return { incident: response.detail, lookupIncidentId: incidentId, activeTab: TAB_ID.Overview, aiQuestionDraft: "", aiThreads: [], notice: copy.notices.incidentLoaded };
        });
      },
      loadKnownIncident: async (incidentId: string) => {
        const trimmedIncidentId = incidentId.trim();
        if (!trimmedIncidentId) {
          setState((current) => ({ ...current, error: copy.notices.incidentIdRequired }));
          return;
        }
        await runBusy(async () => {
          const response = await opsApi.loadIncident(state.session, trimmedIncidentId);
          return {
            incident: response.detail,
            activeTab: TAB_ID.Overview,
            aiQuestionDraft: "",
            aiThreads: [],
            notice: ""
          };
        });
      },
      checkResolveEligibility: async () => {
        if (!state.incident) {
          return;
        }
        await runBusy(async () => {
          try {
            const response = await opsApi.resolveIncident(state.session, state.incident!.incident.id, state.rootCause, state.customerSafeSummary);
            return { incident: response.detail, notice: copy.notices.incidentResolved };
          } catch (error) {
            return {
              error: formatCopy(copy.notices.resolveDenied, {
                reason: readableError(error)
              })
            };
          }
        });
      },
      resolveIncident: async () => {
        if (!state.incident) {
          return;
        }
        await runBusy(async () => {
          const response = await opsApi.resolveIncident(state.session, state.incident!.incident.id, state.rootCause, state.customerSafeSummary);
          return { incident: response.detail, notice: copy.notices.incidentResolved };
        });
      },
      reopenIncident: async () => {
        if (!state.incident) {
          return;
        }
        const reason = nullableText(state.reopenReason);
        if (!reason) {
          setState((current) => ({ ...current, error: copy.forms.reopenReason }));
          return;
        }
        await runBusy(async () => {
          const response = await opsApi.reopenIncident(state.session, state.incident!.incident.id, { reason });
          return { incident: response.detail, notice: copy.notices.incidentReopened };
        });
      },
      recordReadinessGate: async (gateId) => {
        if (!state.incident || !gateId) {
          return;
        }
        await runBusy(async () => {
          const response = await opsApi.recordReadinessGate(state.session, state.incident!.incident.id, gateId, closeoutEvidenceRequest(state, gateId));
          return { incident: response.detail, notice: copy.notices.readinessRecorded };
        });
      },
      transitionCommunication: async (transition) => {
        if (!state.incident || !transition) {
          return;
        }
        await runBusy(async () => {
          const response = await opsApi.transitionCommunication(state.session, state.incident!.incident.id, {
            transition,
            customerSafeSummary: nullableText(state.customerSafeSummary),
            locale: incidentCommunicationLocale(state.incident)
          });
          return { incident: response.detail, notice: copy.notices.readinessRecorded };
        });
      },
      transitionApproval: async (transition) => {
        if (!state.incident || !transition) {
          return;
        }
        await runBusy(async () => {
          const response = await opsApi.transitionApproval(state.session, state.incident!.incident.id, approvalTransitionRequest(state, transition));
          return { incident: response.detail, notice: copy.notices.readinessRecorded };
        });
      },
      invokeOpsAction: async (actionId) => {
        if (!state.incident || !actionId) {
          return;
        }
        const row = actionRows(state.incident, copy).find((candidate) => candidate.id === actionId);
        if (!row) {
          setState((current) => ({ ...current, error: copy.notices.typedActionNotFound }));
          return;
        }
        await runBusy(async () => {
          await opsApi.invokeAction(state.session, state.incident!, row);
          const response = await opsApi.recordReadinessGate(state.session, state.incident!.incident.id, row.gateId, closeoutEvidenceRequest(state, row.gateId, row.id));
          return { incident: response.detail, notice: `${row.buttonLabel} requested` };
        });
      }
    }),
    [copy, handoffIncidentId, runBusy, state]
  );

  return { state, actions };
}

async function loadSession(): Promise<PublicAuthSession> {
  try {
    return await opsApi.getSession();
  } catch {
    return unauthenticatedSession("unavailable");
  }
}

async function loadHealth(): Promise<HealthResponse | null> {
  try {
    return await opsApi.getHealth();
  } catch {
    return null;
  }
}

async function loadPreviewIncident(session: PublicAuthSession, health: HealthResponse | null, incidentQueue: readonly { readonly id: string }[]) {
  const fixtureIncidentId = incidentQueue[0]?.id ?? health?.previewFixtures?.incidentIds?.[0];
  if (!fixtureIncidentId || (!health?.previewFixtures?.enabled && incidentQueue.length === 0)) {
    return null;
  }
  try {
    return await opsApi.loadIncident(session, fixtureIncidentId);
  } catch {
    return null;
  }
}

async function loadInitialIncident(
  session: PublicAuthSession,
  health: HealthResponse | null,
  incidentQueue: readonly { readonly id: string }[],
  handoffIncidentId: string | null
): Promise<{ readonly detail: Awaited<ReturnType<typeof opsApi.loadIncident>>["detail"] | null; readonly handoffFailed: boolean }> {
  if (handoffIncidentId === null) {
    const preview = await loadPreviewIncident(session, health, incidentQueue);
    return { detail: preview?.detail ?? null, handoffFailed: false };
  }
  try {
    const response = await opsApi.loadIncident(session, handoffIncidentId);
    return { detail: response.detail, handoffFailed: false };
  } catch {
    return { detail: null, handoffFailed: true };
  }
}

async function loadIncidentQueue(session: PublicAuthSession | null, filters: IncidentQueueFilters, search: string) {
  try {
    const response = await opsApi.listIncidents(session, {
      search: search || null,
      appId: filters.appId,
      environment: filters.environment,
      severity: filters.severity,
      status: filters.status,
      sort: filters.sort
    });
    return response.incidents;
  } catch {
    return [];
  }
}

function createIncidentRequest(create: CreateIncidentForm): CreateIncidentRequest {
  const serviceHint = create.serviceHint === CUSTOM_SERVICE_OPTION ? create.customServiceHint : create.serviceHint;
  return {
    rawSummary: create.rawSummary,
    appId: create.appId,
    source: create.source as CreateIncidentRequest["source"],
    serviceHint: nullableText(serviceHint),
    environment: nullableText(create.environment),
    customerLocale: create.customerLocale,
    customerAudienceRef: nullableText(create.customerAudienceRef),
    ownerRef: nullableText(create.ownerRef),
    severity: create.severity as CreateIncidentRequest["severity"]
  };
}

function updateCreateForm(create: CreateIncidentForm, name: keyof CreateIncidentForm, value: string, health: HealthResponse | null): CreateIncidentForm {
  if (name === "appId") {
    const catalog = incidentCreateCatalog(health);
    const appId = selectedCatalogAppId(value, catalog);
    return {
      ...create,
      appId,
      environment: defaultEnvironment(appId, catalog),
      serviceHint: defaultServiceHint(appId, catalog),
      customServiceHint: "",
      ownerRef: defaultOwnerRef(catalog)
    };
  }
  if (name === "customerLocale") {
    return { ...create, customerLocale: value !== "es" && value !== "ru" ? "en" : value };
  }
  return { ...create, [name]: value };
}

function updateQueueFilters(filters: IncidentQueueFilters, name: keyof IncidentQueueFilters, value: string): IncidentQueueFilters {
  if (name === "sort") {
    return { ...filters, sort: isQueueSort(value) ? value : "priority" };
  }
  return { ...filters, [name]: value };
}

function closeoutEvidenceRequest(state: OpsCenterState, gateId: string, actionId?: string) {
  const request = {
    rootCause: nullableText(state.rootCause),
    remediationProof: evidenceForGate(gateId, state.remediationProof, [CLOSEOUT_GATE.RemediationProof, CLOSEOUT_GATE.CodeBridge]),
    verificationEvidence: evidenceForGate(gateId, state.verificationEvidence, [CLOSEOUT_GATE.HealthVerification, CLOSEOUT_GATE.VerificationEvidence]),
    customerSafeSummary: evidenceForGate(gateId, state.customerSafeSummary, [
      CLOSEOUT_GATE.OutageNotice,
      CLOSEOUT_GATE.CustomerNotifications,
      CLOSEOUT_GATE.ResolutionNotifications
    ]),
    actionId: actionId ?? null
  };
  return request;
}

function evidenceForGate(gateId: string, value: string, gateIds: readonly string[]): string | null {
  return gateIds.includes(gateId) ? nullableText(value) : null;
}

function isQueueSort(value: string): value is IncidentQueueFilters["sort"] {
  return value === "priority" || value === "customers" || value === "status" || value === "service";
}

function defaultEnvironment(appId: string, catalog = incidentCreateCatalog(null)): string {
  return optionsForApp(catalog.environmentsByApp, appId)[0]?.[0] ?? "localhost";
}

function defaultServiceHint(appId: string, catalog = incidentCreateCatalog(null)): string {
  return optionsForApp(catalog.servicesByApp, appId)[0]?.[0] ?? CUSTOM_SERVICE_OPTION;
}

function defaultOwnerRef(catalog = incidentCreateCatalog(null)): string {
  return catalog.owners[0]?.[0] ?? "employee:maya-chen";
}

function nullableText(value: string): string | null {
  const text = value.trim();
  return text === "" ? null : text;
}

function incidentCommunicationLocale(incident: OpsCenterState["incident"]): "en" | "es" | "ru" | null {
  const locale = incident?.notifications.find((item) => item.locale)?.locale;
  if (locale === "en" || locale === "es" || locale === "ru") {
    return locale;
  }
  return null;
}

function approvalTransitionRequest(state: OpsCenterState, transition: IncidentApprovalTransition): IncidentApprovalTransitionRequest {
  const decision = state.incident?.decisions.find(
    (item) =>
      item.state !== IncidentDecisionState.Resolved &&
      item.state !== IncidentDecisionState.Denied &&
      item.approvalState !== ApprovalState.Approved &&
      item.approvalState !== ApprovalState.Denied
  );
  const serviceOwnerRef = state.incident?.incident.accountability.serviceOwnerRef ?? null;
  return {
    transition,
    decisionId: decision?.id ?? null,
    assignedRole: transition === IncidentApprovalTransition.Reassign ? "owner" : null,
    ownerRef: transition === IncidentApprovalTransition.Reassign || transition === IncidentApprovalTransition.EscalateOwner ? serviceOwnerRef : null,
    reason: approvalTransitionReason(transition)
  };
}

function approvalTransitionReason(transition: IncidentApprovalTransition): string {
  const reasons: Readonly<Record<IncidentApprovalTransition, string>> = {
    [IncidentApprovalTransition.RequestApproval]: "Approval requested from the Ops Center workflow.",
    [IncidentApprovalTransition.Approve]: "Approval granted from the Ops Center workflow.",
    [IncidentApprovalTransition.Reject]: "Approval rejected from the Ops Center workflow.",
    [IncidentApprovalTransition.Reassign]: "Approval reassigned to the service owner.",
    [IncidentApprovalTransition.OwnerAcknowledge]: "Service owner acknowledged incident ownership.",
    [IncidentApprovalTransition.EscalateOwner]: "Owner acknowledgement escalated from the Ops Center workflow."
  };
  return reasons[transition];
}

function unauthenticatedSession(provider: string): PublicAuthSession {
  return {
    ok: true,
    authenticated: false,
    provider,
    loginUrl: "/auth/login",
    logoutUrl: "/auth/logout"
  };
}

function isAuthenticationError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return message === "http_401" || message.includes("authentication_required") || message.includes("session_expired");
}

export function authProviderLabel(state: OpsCenterState, copy: OpsCenterCopy): string {
  if (state.session?.provider === AUTH_PROVIDER_DISABLED) {
    return copy.auth.authDisabled;
  }
  return readableError(state.error || "ops_bridge_auth_unavailable");
}

export function isOperatorAccessCodeSession(session: PublicAuthSession | null): boolean {
  return session?.provider === AUTH_PROVIDER.OperatorAccessCode;
}

export function isMicrosoftEntraSession(session: PublicAuthSession | null): boolean {
  return session?.provider === AUTH_PROVIDER.MicrosoftEntra;
}

export function previewFixtureMode(health: HealthResponse | null): string {
  return health?.previewFixtures?.mode ?? PREVIEW_FIXTURE_FALLBACK_MODE;
}
